import { createApi, FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query/react';
import {
  GetThemeManifestArgs,
  NormalizeThemeArgs,
  ThemeManifestCache,
  ThemeNormalizationResult,
} from './theme-contract.api.types';
import { themeContractBaseQuery } from './theme-contract.base-query';
import {
  fromApiThemeManifest,
  fromApiThemeValidation,
  normalizeThemeBaseUrl,
  toApiThemeValidation,
} from './theme-contract.mapper';

export const ThemeContractService = createApi({
  reducerPath: 'ThemeContractService',
  baseQuery: themeContractBaseQuery,
  endpoints: (builder) => ({
    getThemeManifest: builder.query<ThemeManifestCache, GetThemeManifestArgs>({
      serializeQueryArgs: ({ queryArgs }) => ({ baseUrl: normalizeThemeBaseUrl(queryArgs.baseUrl) }),
      async queryFn(
        args,
        api,
        _extraOptions,
        baseQuery,
      ): Promise<QueryReturnValue<ThemeManifestCache, FetchBaseQueryError, FetchBaseQueryMeta>> {
        const select = ThemeContractService.endpoints.getThemeManifest.select(args);
        const previous = select(api.getState() as Parameters<typeof select>[0]).data;
        const result = await baseQuery({
          url: `${normalizeThemeBaseUrl(args.baseUrl)}/theme-contract/schemas/1`,
          cache: 'no-cache',
          headers: previous?.etag ? { 'If-None-Match': previous.etag } : {},
          validateStatus: (response) => response.ok || response.status === 304,
        });
        if (result.error) return { error: result.error, meta: result.meta };
        if (result.meta?.response?.status === 304) {
          return previous
            ? { data: previous, meta: result.meta }
            : { error: { status: 'CUSTOM_ERROR', error: 'manifest_unavailable' }, meta: result.meta };
        }
        const manifest = fromApiThemeManifest(result.data);
        return manifest
          ? { data: { manifest, etag: result.meta?.response?.headers.get('ETag') ?? null }, meta: result.meta }
          : { error: { status: 'CUSTOM_ERROR', error: 'invalid_manifest' }, meta: result.meta };
      },
    }),
    normalizeTheme: builder.mutation<ThemeNormalizationResult, NormalizeThemeArgs>({
      async queryFn(args, _api, _extraOptions, baseQuery) {
        const result = await baseQuery({
          url: `${normalizeThemeBaseUrl(args.baseUrl)}/theme-contract/schemas/1/validate`,
          method: 'POST',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(toApiThemeValidation(args)),
          validateStatus: (response) => response.ok || response.status === 422,
        });
        if (result.error) return { error: result.error, meta: result.meta };
        const data = fromApiThemeValidation(result.data);
        if (!data || (result.meta?.response?.status === 422 && data.success)) {
          return { error: { status: 'CUSTOM_ERROR', error: 'invalid_validation_response' }, meta: result.meta };
        }
        return { data, meta: result.meta };
      },
    }),
  }),
});

export const { useLazyGetThemeManifestQuery, useNormalizeThemeMutation } = ThemeContractService;
